import {Component, inject, OnDestroy, OnInit, Optional} from '@angular/core';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {ContentInfo} from '../../core/definitions/content-info';
import {CurrentObjectService} from '../../core/current-object.service';
import {SelectorContainer} from '../../core/definitions/selector-container';
import {SearchSelectorService} from '../../core/search-selector.service';
import {TemplateSelectorContainer} from '../../core/definitions/template-selector-container';
import {UiToolsService} from '../../core/ui-tools.service';
import {MediaHelperService} from '../../core/media-helper.service';
import {OperationAndSectionsContainerParams, OperationService} from '../../operations/operation.service';
import {OperationDialogService} from '../../operations/operation-dialog.service';
import {CmsApiService} from '../../core/cms-api.service';
import {SpectrumProcedureStep} from '../../core/definitions/setting-spectrum-procedure';
import {OperationDef} from '../../core/definitions/operation-def';
import {Selector} from '../../core/definitions/reference';
import {OperationStepType} from '../../core/definitions/operation-step-type.enum';
import {TranslateService} from '@ngx-translate/core';
import {OperationTarget} from '../../core/definitions/operation-target.enum';
import {ContentMenusService} from '../../object-content-tab/content-menus.service';
import {OperationContainer} from '../../core/definitions/operation-container';
import {ContentListSourceContainer} from '../../core/definitions/object-content-tab/content-list-source-container';
import {SelectorCreationParams} from '../../core/definitions/selector-creation-params';
import {OperationExecutorType} from '../../core/definitions/operation-executor-type.enum';
import {PrimusRouterService} from '../../core/primus-router.service';
import {PrimusRouteService} from '../../core/primus-route.service';
import {Subscription} from 'rxjs';
import {CurrentSearchContext} from "../../object-search/current-search.context";
import {RefService} from "../../core/ref.service";
import {ActivatedRoute, Router} from "@angular/router";
import {ObjectPageNavigationStack} from "../../object-page-v2/object-page-navigation-stack";
import {FeatureFlagsService} from "../../core/feature-flags.service";
import {StateParams} from "../../core/definitions/state-params";
import {RefParams} from "../../core/definitions/ref-params";
import {RefData} from "../../core/definitions/ref-data";

@Component({
    selector: 'app-procedure',
    templateUrl: './procedure.component.html',
    styleUrls: ['./procedure.component.scss'],
    animations: [
        trigger('showAllFields', [
            state('closedTop', style({ height: '150px'})),
            state('openTop', style({ height: '*'})),
            transition('closedTop => openTop', [
                style({ overflow: 'hidden' }),
                animate('300ms 100ms linear', style({ height: '*' }))
            ]),
            transition('openTop => closedTop', [
                style({ overflow: 'hidden' }),
                animate('200ms linear', style({ height: '150px' }))
            ]),
        ]),
        trigger('showContentMenu', [
          state('collapseMenu', style({ width: '0' })),
          state('expandMenu', style({ width: '80px' })),
          transition('collapseMenu => expandMenu', [
              animate('300ms ease', style({ width: '80px' }))
          ]),
          transition('expandMenu => collapseMenu', [
              animate('300ms ease', style({ width: '0' }))
          ]),
      ])
    ],
    standalone: false
})
export class ProcedureComponent implements OnInit, OnDestroy {
  // Injections
  public readonly currentObjectService: CurrentObjectService = inject(CurrentObjectService);
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly contentMenusService: ContentMenusService = inject(ContentMenusService);
  private readonly featureFlagService: FeatureFlagsService = inject(FeatureFlagsService);
  private readonly mediaHelper: MediaHelperService = inject(MediaHelperService);
  private readonly objectPageNavigationStack: ObjectPageNavigationStack = inject(ObjectPageNavigationStack);
  private readonly operationDialogService: OperationDialogService = inject(OperationDialogService);
  private readonly operationService: OperationService = inject(OperationService);
  private readonly primusRoute: PrimusRouteService = inject(PrimusRouteService);
  private readonly primusRouter: PrimusRouterService = inject(PrimusRouterService);
  private readonly ref: RefService = inject(RefService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly searchSelectorService: SearchSelectorService = inject(SearchSelectorService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly uiTools: UiToolsService = inject(UiToolsService);

  constructor(@Optional() public currentSearchContext?: CurrentSearchContext) {}

  showAllFieldInformation = false;
  loadingProcedureInfo: boolean;
  noProcedureId = false;
  artifactId: string;
  templateSelectorContainer = new TemplateSelectorContainer();
  smallScreen: boolean;
  openMenu = false;
  showImageOrder = false;
  loadFailed = false;
  operationContainer: OperationContainer;
  currentProcedureStep: SpectrumProcedureStep;
  procedureSteps: SpectrumProcedureStep[];
  currentProcedureContentViewName = 'steps';

  private templateGroupId;
  private stopSizeWatch;
  private routerSubscription: Subscription;

  ngOnInit() {
    this.routerSubscription = this.primusRouter.navigationHandler(() => {
      this.init().then();
    });
    this.init().then();
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.stopSizeWatch) {
      this.uiTools.removeWindowSizeListener(this.stopSizeWatch);
    }
  }

  async onBackClick() {
    const currentIdx = this.currentSearchContext?.findResultIndex(this.artifactId);
    if (currentIdx > 0) {
      const prevArtifact = this.currentSearchContext?.searchResults?.[currentIdx - 1];
      const params = new RefParams(prevArtifact, {
        listName: null
      } as StateParams);
      params.searchItemIndex = currentIdx - 1;
      params.rootObjId = prevArtifact.artifact_id;
      const refData = await this.ref.makeRef(params);
      if (refData.stateName.endsWith('artifact')) {
        this.goToArtifact(prevArtifact.artifact_id)
      } else {
        this.goToNotArtifact(refData)
      }
    }
  }

  async onForwardClick() {
    const currentIdx = this.currentSearchContext?.findResultIndex(this.artifactId);
    if (currentIdx < this.currentSearchContext?.resultSize - 1) {
      const nextArtifact = this.currentSearchContext?.searchResults?.[currentIdx + 1];
      const params = new RefParams(nextArtifact, {
        listName: null
      } as StateParams);
      params.searchItemIndex = currentIdx + 1;
      params.rootObjId = nextArtifact.artifact_id;
      const refData = await this.ref.makeRef(params);
      if (refData.stateName.endsWith('artifact')) {
        this.goToArtifact(nextArtifact.artifact_id)
      } else {
        this.goToNotArtifact(refData)
      }
    }
  }

  onCloseClick() {
    const prevRoute = this.objectPageNavigationStack.pop();
    if (this.currentSearchContext) {
      this.router.navigate(['/search'], {
        queryParams: {
          artifactId: null,
        },
        queryParamsHandling: 'merge'
      })
    } else if (prevRoute) {
      this.router.navigateByUrl(prevRoute);
    } else {
      this.router.navigate(['/'])
    }
  }

  private goToArtifact(artifactId: string) {
    if (!location.pathname.startsWith('/search/artifact')) {
      this.router.navigate(['/artifact', artifactId], {
        queryParams: {
          artifactId
        },
        queryParamsHandling: 'merge'
      }).then();
    }
    else {
      this.router.navigate(['..', artifactId], {
        relativeTo: this.route,
        queryParams: {
          artifactId
        },
        queryParamsHandling: 'merge'
      })
    }
  }

  private goToNotArtifact(refData: RefData) {
    this.router.navigate(refData.routerLink, {
      queryParams: refData.queryParams,
      queryParamsHandling: 'merge'
    })
  }

  onStepSelected(step: SpectrumProcedureStep) {
    this.currentProcedureStep = step;
    this.operationContainer.contentInfo.setContentListSource(step.contentListSource);
  }

  onSelectView(viewName: string) {
    this.currentProcedureContentViewName = viewName;
    const ci = this.operationContainer.contentInfo;
    if (viewName === 'steps') {
      ci.setContentListSource(this.currentProcedureStep.contentListSource);
    } else {
      ci.setContentListSource(ci.sources['spectrum_procedure_object']);
    }
  }

  onTemplateSelectorCreated(templateSelectorContainer) {
    this.templateSelectorContainer = templateSelectorContainer;
  }

  async onChangeTemplateGroup(newGroupId) {
    await this.primusRouter.navigateState(this.primusRouter.currentState(),
      {template_group_id: newGroupId},
      {location: 'replace', reload: true});
  }

  async onSelectOperation(operation: OperationDef) {
    this.operationService.setSelectedOperation(this.operationContainer, operation);
    this.operationService.setCurrentOperation(this.operationContainer, operation);

    if (this.smallScreen) {
      this.onToggleMenu();
    }
    this.setCurrentList(this.operationContainer.currentOperation.content_list);
    if (this.operationContainer.contentInfo.curListName === 'spectrum_procedure_object') {
      await this.setProcedureSteps();
    }
  }

  onToggleMenu() {
    this.openMenu = !this.openMenu;
  }

  onToggleChangeImageOrder(/*images?*/) {
    this.showImageOrder = !this.showImageOrder;
  }

  toggleFieldInformation() {
    this.showAllFieldInformation = !this.showAllFieldInformation;
  }

  async loadProcedure() {
    this.loadingProcedureInfo = true;
    if (this.artifactId) {
      try {
        this.operationContainer = await this.operationService.createOperationAndRootSectionsContainer(
          this.getCreateParams());
        await this.postLoadOperations();
      } catch (reason) {
        this.loadFailed = true;
        console.error('Failed loading object: ' + reason.error?.message || reason.message);
      }
    } else {
      this.loadingProcedureInfo = false;
      this.noProcedureId = true;
    }
  }

  selectorOverlay() {
    if (this.templateSelectorContainer?.selectorEnabled) {
      this.searchSelectorService.disableSelector(this.templateSelectorContainer);
    } else {
      this.disableSelector();
    }
  }

  get contentListHeadline() {
    let res = '';
    if (this.operationContainer.contentInfo.curContentListSource) {
      res = this.operationContainer.contentInfo.curContentListSource.caption;
      if (res.startsWith('TRANS__')) {
        res = this.translateService.instant(res);
      }
    }
    return res;
  }

  get count() {
    let res = 0;
    if (this.operationContainer.contentInfo.curContentListSource.searchContainer?.searchResult?.search_count) {
      res = this.operationContainer.contentInfo.curContentListSource.searchContainer?.searchResult?.search_count
    } else if (this.operationContainer.contentInfo.curContentListSource.array.length) {
      res = this.operationContainer.contentInfo.curContentListSource.array.length;
    }
    return res;
  }

  private async init() {
    this.currentObjectService.isEditing = this.primusRoute.params.edit === 'true';
    this.artifactId = this.primusRoute.params.artifactId;
    this.templateGroupId = this.primusRoute.params.template_group_id;
    const windowSize = this.uiTools.windowSize;
    this.smallScreen = windowSize.width < 800;
    this.stopSizeWatch = this.uiTools.addWindowSizeListener(newVal => {
        this.smallScreen = newVal.width < 800;
      }
    );
    await this.loadProcedure();
  }

  private getCreateParams(): OperationAndSectionsContainerParams {
    const params = {
      useExistingObject: true,
      objectId: this.artifactId,
      getSourceObject: !this.currentObjectService.isEditing,
      operationTarget: OperationTarget.PROCEDURE_VIEW,
      templateGroupId: this.templateGroupId
    } as OperationAndSectionsContainerParams;
    if (this.currentObjectService.isEditing) {
      params.executorType = OperationExecutorType.ARTIFACT;
      params.operationStepType = OperationStepType.EDIT_OBJECT;
    }
    return params;
  }

  private setSelectorUsed(selector: Selector) {
    const contentListSources: ContentListSourceContainer[] = [];
    if (this.procedureSteps) {
      this.procedureSteps.forEach(procedureStep => {
        if (procedureStep.artifact_id !== this.currentProcedureStep.artifact_id) {
          contentListSources.push(procedureStep.contentListSource);
        }
      });
    }
    this.operationContainer.contentInfo.setSelectorUsed(selector, contentListSources);
  }

  private enableSelector(selector: Selector, mc: SelectorContainer, params?: SelectorCreationParams) {
    this.setSelectorUsed(selector);
    this.searchSelectorService.enableSelector(selector, mc, params,
      {
        searchContainerCreated: () => {
        },
        selectorCallback: (selectedObj: any) => {
          this.searchSelectorService.disableSelector(mc);
          mc.selectorCallback(selectedObj, selector, () => {
            this.loadProcedure().then();
          });
        }
      }
    );
  }

  private disableSelector() {
    this.searchSelectorService.disableSelector(<SelectorContainer>this.operationContainer);
  }

  private async postLoadOperations() {
    const sectionsContainer = this.operationContainer.rootSectionsContainer;
    let listName = this.primusRoute.params.listName;
    await this.setOperationContainer();
    this.currentObjectService.currentObject = sectionsContainer.rootObject;
    this.operationContainer.contentInfo.updateContentInfo(
      this.currentObjectService.isEditing, sectionsContainer.rootObject, listName);
    if (this.operationContainer.contentInfo.curListName) {
      listName = this.operationContainer.contentInfo.curListName;
    }
    this.operationContainer.contentInfo.templateGroupId = this.templateGroupId;
    if (!this.operationContainer.contentInfo.curListName) {
      this.operationContainer.contentInfo.curListName = listName;
    }
    await this.getMedia();

    this.operationContainer.contentInfo.setContentMenus = () => {
      this.setContentMenus();
    };
    await this.setContentMenus();
    for (const element of this.operationContainer.operations) {
      const operation = element;
      if (operation.content_list !== 'overview' && operation.content_list === this.operationContainer.contentInfo.curListName) {
        await this.onSelectOperation(operation);
        break;
      }
    }
    this.loadingProcedureInfo = false;
  }

  private async setProcedureSteps() {
    const setting = await this.cms.getSpectrumProcedureSetting({
      spectrum_procedure_id: this.operationContainer.rootSectionsContainer.rootObject.superobject_type_id
    });
    this.procedureSteps = setting.procedure_steps;
    const procedureSource = this.operationContainer.contentInfo.sources['spectrum_procedure_object'];
    this.procedureSteps.forEach((step, index) => {
      step.contentListSource = procedureSource.subMenus[index];
    });

    if (this.procedureSteps?.length) {
      this.onStepSelected(this.procedureSteps[0]);
      if (this.primusRoute.params.listName === 'spectrum_procedure_object') {
        if (this.primusRoute.params.stepName) {
          this.procedureSteps.forEach(procedureStep => {
            if (procedureStep.procedure_object_status_id_value === this.primusRoute.params.stepName) {
              this.onStepSelected(procedureStep);
            }
          });
        } else {
          await this.primusRouter.navigateState(this.primusRouter.currentState(),
            {stepName: this.currentProcedureStep.procedure_object_status_id_value},
            {location: 'replace', reload: false});
        }
      }
    }
  }

  private async setOperationContainer() {
    this.operationContainer.art = this.operationContainer.rootSectionsContainer.rootObject;
    this.operationContainer.contentInfo = new ContentInfo();
    this.operationContainer.openOperationDialogFn = () => {
      this.operationContainer.currentOperation.$$showOperationView = true;
      this.operationDialogService.openOperationDialog(this.operationContainer).then(() => {
        this.loadProcedure().then();
      });
    };
    this.operationContainer.enableSelector = (selector, params) => {
      this.enableSelector(selector, this.operationContainer, params);
    };
    this.operationContainer.disableSelector = () => {
      this.disableSelector();
    };
  }

  private async setContentMenus() {
    const contentInfo = this.operationContainer.contentInfo;
    contentInfo.setCurrentList = (listName: string) => {
      this.setCurrentList(listName);
    };
    if (!contentInfo.menus) {
      contentInfo.menus = await this.contentMenusService.getMenus(contentInfo);
    }
    await this.contentMenusService.setVerticalContentMenus(contentInfo.menus, contentInfo);
  }

  private setCurrentList(listName) {
    const ci = this.operationContainer.contentInfo;
    ci.curListName = listName;
    ci.curListContainer = null;
    ci.setContentListSource(null);
    if (ci.menus) {
      this.contentMenusService.setActiveVerticalMenu(ci.menus, ci);
    }
    if (ci.contentListContainers[listName]) {
      this.contentMenusService.runListSearch(ci.curListName, ci).then();
    } else if (ci.sources?.[listName]) {
      ci.setContentListSource(ci.sources[listName]);
    }
  }

  private async getMedia() {
    const object = this.operationContainer.rootSectionsContainer.rootObject;
    object.$$mediaContainer = await this.mediaHelper.getMediaContainerForAllObjectMedia(object);
  }

  readonly newLayout = this.featureFlagService.getFeatureFlags()?.experimental.useNewMainMenu
}
